<template>
  <router-view/>
  
  <!-- Modal de inactividad -->
  <InactivityModal 
    v-model="showInactivityModal"
    @continue="handleContinueSession"
    @logout="handleLogoutSession"
    @timeout="handleTimeout"
  />
</template>

<script>
import { watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import InactivityModal from '@/components/InactivityModal.vue'
import useInactivityDetector from '@/composables/useInactivityDetector'
import { isAnyFormDirty } from '@/utils/formDirtyGuard'

export default {
  name: 'App',
  components: {
    InactivityModal
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    
    // Evita perder cambios clínicos sin guardar ante recarga/cierre manual
    // (el flujo de actualización de versión tiene su propia confirmación).
    const handleBeforeUnload = (event) => {
      if (isAnyFormDirty()) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    // Limpia el cache-busting ?_v= de la barra de direcciones tras una
    // actualización forzada para no dejar parámetros muertos en la URL.
    const cleanVersionParam = () => {
      if (new URLSearchParams(window.location.search).has('_v')) {
        window.history.replaceState({}, '', window.location.pathname + window.location.hash)
      }
    }

    onMounted(() => {
      cleanVersionParam()
      window.addEventListener('beforeunload', handleBeforeUnload)
    })

    onUnmounted(() => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    })

    // Configurar detector de inactividad (60 minutos = 3600000 ms)
    // Para testing: usar valores menores (ej: 1 * 60 * 1000 = 1 minuto)
    const { 
      showModal: showInactivityModal, 
      resetTimer, 
      stopTimer, 
      startTimer,
      closeModal 
    } = useInactivityDetector(60 * 60 * 1000)
    
    /**
     * Maneja cuando el usuario decide continuar con la sesión
     */
    const handleContinueSession = () => {
      closeModal()
      resetTimer()
    }
    
    /**
     * Maneja cuando el usuario decide cerrar sesión manualmente
     */
    const handleLogoutSession = () => {
      closeModal()
      stopTimer()
    }
    
    /**
     * Maneja cuando se agota el tiempo del modal
     */
    const handleTimeout = () => {
      closeModal()
      stopTimer()
    }
    
    /**
     * Controla el ciclo de vida del timer de inactividad según la ruta actual
     * - Rutas públicas (login, registro, etc.): Timer detenido
     * - Rutas privadas con token válido: Timer activo
     */
    watch(() => route.name, (newRouteName) => {
      const token = store.getters['auth/getToken']
      
      // Definir rutas públicas donde no debe correr el timer
      const publicRoutes = ['auth-login', 'auth-register', 'auth-recover-password']
      
      if (publicRoutes.includes(newRouteName)) {
        // Estamos en una ruta pública, detener el timer
        stopTimer()
      } else if (token) {
        // Estamos en una ruta privada y hay token, iniciar/reiniciar el timer
        startTimer()
      } else {
        // No hay token en ruta privada (raro, pero por seguridad detener timer)
        stopTimer()
      }
    }, { immediate: true })
    
    return {
      showInactivityModal,
      handleContinueSession,
      handleLogoutSession,
      handleTimeout
    }
  }
}
</script>
